import React, { type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';
import EyeOpen from '@atlaskit/icon/core/eye-open';
import EyeOpenFilledIcon from '@atlaskit/icon/core/eye-open-filled';
import WatchIcon from '@atlaskit/icon/glyph/watch';
import { colors } from '@atlaskit/theme';
import { B400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import AkTooltip from '@atlaskit/tooltip';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import TransitiveNumber from '@atlassian/react-transitive-number';
import messages from './messages.tsx';

export type OwnProps = {
	isDropdownOpen: boolean;
	onClick?: (event: SyntheticEvent) => void;
	onOpenDropdownAnalytics: (arg1: number, arg2: UIAnalyticsEvent) => void;
};

export type ActionButtonProps = {
	isWatching: boolean;
	isKeyboardShortcutEnabled: boolean;
	canViewDropdown: boolean;
} & OwnProps & {
		watchersCount: number;
		onToggleWatching: (arg1: SyntheticEvent, arg2: UIAnalyticsEvent) => void;
	};

type GetIsWatchngColorProps = {
	isWatching: boolean;
	isDropdownOpen: boolean;
};

type GetTooltipMessageProps = {
	isKeyboardShortcutEnabled: boolean;
	isWatching: boolean;
	canViewDropdown: boolean;
};

const getCounterDisplay = (watchersCount: number) => {
	const maxValue = 99;

	if (watchersCount <= 0) {
		return '';
	}

	if (watchersCount > maxValue) {
		return `${maxValue}+`;
	}

	return watchersCount;
};

const getIsWatchingColor = ({ isDropdownOpen, isWatching }: GetIsWatchngColorProps) => {
	if (!isDropdownOpen && isWatching) {
		return token('color.text.brand', colors.B500);
	}
	return undefined;
};

const getTooltipMessage = ({
	canViewDropdown,
	isKeyboardShortcutEnabled,
	isWatching,
}: GetTooltipMessageProps) => {
	if (canViewDropdown) {
		return messages.watchersOptionsTooltip;
	}

	if (isKeyboardShortcutEnabled) {
		return isWatching ? messages.unwatchTooltipWithHotkey : messages.watchTooltipWithHotkey;
	}
	return isWatching ? messages.unwatchTooltip : messages.watchTooltip;
};

export const ActionButton = ({
	isWatching,
	isDropdownOpen,
	isKeyboardShortcutEnabled,
	canViewDropdown,
	watchersCount,
	onToggleWatching,
	onClick,
	onOpenDropdownAnalytics,
}: ActionButtonProps) => {
	const { formatMessage } = useIntl();

	const getOnClick = () => {
		if (canViewDropdown) {
			return (clickEvent: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
				if (onClick && typeof onClick === 'function') {
					onClick(clickEvent);
				}
				return !isDropdownOpen && onOpenDropdownAnalytics(watchersCount, analyticsEvent);
			};
		}

		return onToggleWatching;
	};

	const watchedIconOld = (
		<WatchIcon
			testId="issue-view-watchers.watchers.action-button.watch-icon"
			label=""
			size="medium"
			primaryColor={token('color.icon.selected', B400)}
		/>
	);

	const unwatchedIconOld = (
		<WatchIcon
			testId="issue-view-watchers.watchers.action-button.watch-icon"
			size="medium"
			label=""
		/>
	);

	const watchedIcon = (
		<EyeOpenFilledIcon
			testId="issue-view-watchers.watchers.action-button.watch-icon"
			label=""
			color={token('color.icon.selected', B400)}
			spacing="spacious"
		/>
	);

	const unwatchedIcon = (
		<EyeOpen
			testId="issue-view-watchers.watchers.action-button.watch-icon"
			label=""
			color={token('color.icon.selected', B400)}
			spacing="spacious"
		/>
	);

	const getWatchIconContent = () => {
		const iconOld = isWatching ? watchedIconOld : unwatchedIconOld;
		const icon = isWatching ? watchedIcon : unwatchedIcon;

		const buttonAriaLabel = isWatching
			? formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.watchingLabelIssueTermRefresh
						: messages.watchingLabel,
					{ watchersCount },
				)
			: formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.notWatchingLabelIssueTermRefresh
						: messages.notWatchingLabel,
					{ watchersCount },
				);

		return (
			<Container
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				data-testid="issue.watchers.action-button.root"
			>
				<Button
					appearance="subtle"
					spacing="default"
					onClick={getOnClick()}
					isSelected={isDropdownOpen}
					aria-label={buttonAriaLabel}
					aria-expanded={isDropdownOpen}
					interactionName="auto-generated.jira-issue-view-watchers.watchers.view.action-button.button"
				>
					{/* react-transitive-number animation doesn't work if a different ancestor
                                    styled component is used during re-render. That's why we're forced to use a
                                    'style' attribute on the element bellow. */}
					<ButtonContent
						// eslint-disable-next-line jira/react/no-style-attribute
						style={{ color: getIsWatchingColor({ isDropdownOpen, isWatching }) }}
					>
						<ButtonIcon>{isVisualRefreshEnabled() ? icon : iconOld}</ButtonIcon>
						<Counter
							aria-hidden="true"
							// eslint-disable-next-line jira/integration/test-id-by-folder-structure
							data-testid="issue.watchers.action-button.counter"
							hasWatchers={watchersCount !== 0}
						>
							<TransitiveNumber>{getCounterDisplay(watchersCount)}</TransitiveNumber>
						</Counter>
					</ButtonContent>
				</Button>
			</Container>
		);
	};

	return (
		<AkTooltip
			/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
			testId="issue.watchers.action-button.tooltip"
			content={formatMessage(
				getTooltipMessage({ canViewDropdown, isKeyboardShortcutEnabled, isWatching }),
			)}
			position="top"
		>
			{getWatchIconContent()}
		</AkTooltip>
	);
};

export default ActionButton;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> button': {
		padding: `0 ${token('space.050', '4px')}`,
		display: 'flex',
		alignItems: 'center',
	},
	display: 'flex',
	justifyContent: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonContent = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonIcon = styled.span({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Counter = styled.span<{ hasWatchers?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginLeft: (props) => () =>
		props.hasWatchers ? token('space.025', '2px') : token('space.0', '0px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minWidth: (props) => (props.hasWatchers ? '15px' : '0'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const visibleForTesting = {
	Counter,
};
